import { FC, useMemo, useState } from 'react'
import { CurecallBadge, ECurecallBadgeColor } from '@honestica/curecall-ui'
import { useTranslation } from 'react-i18next'
import { LocalizedLink } from 'src/components/navigation/localized-link.component'
import { getFormattedDate } from 'src/utils/date-formatter.util'
import { formatPhone } from 'src/utils/phone.util'
import { getFullName } from 'src/utils/string-formatter.util'
import { TBatchImportLogResult, TBatchImportPatient } from './batch-import.type'
import { EBatchImportTier } from './batch-import-errors.util'
import { getBatchImportRowReport } from './batch-import-report.util'

type Props = {
  projectId: number
  rowIds: number[]
  results: Record<number, TBatchImportLogResult>
}

const BatchImportPatientCell: FC<{ patient: TBatchImportPatient }> = ({ patient }) => {
  const fullName = getFullName({ firstname: patient.firstname, lastname: patient.lastname }).trim()
  // birthdate is a calendar day: format it in UTC so the day doesn't shift with the viewer's timezone
  const birthdate = patient.birthdate ? getFormattedDate(patient.birthdate, 'UTC') : undefined
  const secondaryInfos = [
    patient.remoteId,
    birthdate,
    patient.phone && (formatPhone(patient.phone) || patient.phone),
  ]
    .filter(Boolean)
    .join(' · ')

  return (
    <>
      {fullName && <div className="c-font-bold">{fullName}</div>}
      {secondaryInfos && <div className="c-text-sm c-text-grey-700">{secondaryInfos}</div>}
    </>
  )
}

const BatchImportRow: FC<{ projectId: number; result?: TBatchImportLogResult }> = ({ projectId, result }) => {
  const { t } = useTranslation()

  const report = getBatchImportRowReport(t, result)

  let status: React.ReactNode
  if (report.tier === undefined) {
    status = (
      <CurecallBadge className="tw-w-24 tw-text-center" color={ECurecallBadgeColor.GREY}>
        {report.statusLabel}
      </CurecallBadge>
    )
  } else if (report.tier === EBatchImportTier.NOMINAL) {
    status = (
      <CurecallBadge className="tw-w-24 tw-text-center" color={ECurecallBadgeColor.GREEN}>
        {report.statusLabel}
      </CurecallBadge>
    )
  } else if (report.tier === EBatchImportTier.WARNING) {
    // the UI library has no orange badge
    status = (
      <div className="c-w-24 c-text-center c-rounded-full c-border-2 c-px-1 c-py-0.5 c-font-nexa c-text-xs c-font-semibold c-leading-none c-tracking-wide c-border-orange-200 c-bg-orange-200 c-text-orange-800">
        {report.statusLabel}
      </div>
    )
  } else {
    status = (
      <CurecallBadge className="tw-w-24 tw-text-center" color={ECurecallBadgeColor.RED}>
        {report.statusLabel}
      </CurecallBadge>
    )
  }

  // the raw backend message stays available on hover for support
  const details = report.details.map((detail, detailIndex) => (
    <div key={detailIndex} title={detail.rawMessage}>
      {detail.text}
    </div>
  ))

  return (
    <tr className="c-border-b c-border-grey-200 last:c-border-0">
      <td className="c-py-2 c-pr-4">{result?.sourceLine}</td>
      <td className="c-py-2 c-pr-4">
        {result?.patient && <BatchImportPatientCell patient={result.patient} />}
      </td>
      <td className="c-py-2 c-pr-4">{status}</td>
      <td className="c-py-2 c-pr-4">{details}</td>
      <td className="c-py-2">
        {result?.PatientId && (
          <LocalizedLink
            to={`/${projectId}/medical/patients/${result.PatientId}`}
            className="c-text-primary c-underline"
          >
            {t('components.file-uploader.batch-import-view-patient')}
          </LocalizedLink>
        )}
      </td>
    </tr>
  )
}

export const BatchImportTable: FC<Props> = ({ projectId, rowIds, results }) => {
  const { t } = useTranslation()
  const [isSourceLineAscending, setIsSourceLineAscending] = useState(true)

  // Rows without a source line (pending or not provided by the backend) go last, in their original order
  const sortedRows = useMemo(() => {
    const direction = isSourceLineAscending ? 1 : -1
    return rowIds
      .map((interopLogId, index) => ({ interopLogId, index }))
      .sort((rowA, rowB) => {
        const lineA = results[rowA.interopLogId]?.sourceLine
        const lineB = results[rowB.interopLogId]?.sourceLine
        if (lineA === undefined && lineB === undefined) return rowA.index - rowB.index
        if (lineA === undefined) return 1
        if (lineB === undefined) return -1
        return (lineA - lineB) * direction || rowA.index - rowB.index
      })
  }, [rowIds, results, isSourceLineAscending])

  return (
    <table className="c-w-full c-text-left c-mt-2">
      <thead>
        <tr className="c-border-b c-border-grey-300">
          <th className="c-py-2 c-pr-4" aria-sort={isSourceLineAscending ? 'ascending' : 'descending'}>
            <button
              type="button"
              className="c-font-bold c-flex c-items-center c-gap-1"
              onClick={() => setIsSourceLineAscending((previous) => !previous)}
            >
              {t('components.file-uploader.batch-import-column-source-line')}
              <span aria-hidden="true">{isSourceLineAscending ? '▲' : '▼'}</span>
            </button>
          </th>
          <th className="c-py-2 c-pr-4">{t('components.file-uploader.batch-import-column-patient')}</th>
          <th className="c-py-2 c-pr-4">{t('components.file-uploader.batch-import-column-status')}</th>
          <th className="c-py-2 c-pr-4">{t('components.file-uploader.batch-import-column-details')}</th>
          <th className="c-py-2">{t('components.file-uploader.batch-import-column-patient-file')}</th>
        </tr>
      </thead>
      <tbody>
        {sortedRows.map(({ interopLogId }) => (
          <BatchImportRow key={interopLogId} projectId={projectId} result={results[interopLogId]} />
        ))}
      </tbody>
    </table>
  )
}
